import * as _ from 'lodash';
import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { applySecretKeyMask } from '../core/secret-key-mask';
import { randomFirstName, randomLastName, randomName, randomIdentity } from '../constants/fakenames';
import { _SAMPLE_INVITATION_CODES } from '../constants/fakeinvitations/codes';
import { IAccountInfo } from './view-mng-inst-accounts/view-mng-inst-accounts.component';
import { AuthService, IUserInfo } from '../api/auth.service';
import { FormControl } from '@angular/forms';

// This service can be used to mix demo data with real-time database data as required

// INTERFACES
export interface IAccountFakeInfo {
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  isAccommCoord: boolean,
  numSessions: number,
  accountCreatedOn: moment.Moment,
  invitationCode: string,
  invitationName: string,
  invitationEmail: string,
  accessGrantedOn: moment.Moment,
  isInviteUsed: boolean,
}

export interface IBooking {
  __isSelected?: FormControl,
  name: string,
  oct_id: string,
  timestamp: moment.Moment,
  reqTransferTimestamp?: moment.Moment,
}
export interface IAvailableSession {
  __isStartingSoon?: boolean,
  __dateTimeStartStr?: string,
  __num: number,
  id: number,
  // location
  room: string,
  campusBuilding: string,
  address: string,
  city: string,
  province: string, 
  postalCode: string,
  phoneNumber: string,
  facultyId: number,
  // time
  dateTimeStart: moment.Moment,
  // capacity
  capacity: number,
  // restrictions
  isAccessCodeEnabled: boolean,
  accessCode: string,
  isHidden: boolean,
  // assigned invigil
  inivigilatorId: number,
  //
  status?: string,
  // invitation code
  invitationCode: string,
  // bookings
  bookingsCount: number,
  bookings: IBooking[],
  waitlistCount: number,
  waitlist: IBooking[],
}

// DATA
let SAMPLE_INVITATION_CODES = _SAMPLE_INVITATION_CODES;

// UTILITY FUNCTIONS
const randInt = (from, to) => {
  return Math.floor( from + (to-from)*Math.random() );
}
const randArrEntry = (arr:any[]) => {
  const i = Math.floor(arr.length * Math.random());
  return arr[i];
}
const coinFlip = () => {
  return (Math.random() > 0.5);
}
const getInvitCode = () => {
  const i = Math.floor(SAMPLE_INVITATION_CODES.length * Math.random());
  const str = SAMPLE_INVITATION_CODES[i];
  if (SAMPLE_INVITATION_CODES.length === 0){
    SAMPLE_INVITATION_CODES = _SAMPLE_INVITATION_CODES;
  }
  SAMPLE_INVITATION_CODES.splice(i, 1);
  return applySecretKeyMask(str);
}
const randomPastDate = (dayVary?:number) => {
  dayVary = dayVary || randInt(5, 10);
  const time = [randInt(9, 18), 15*randInt(0, 3)].join(':');
  return moment(time, 'H:m').subtract(dayVary, 'days');
}
const dummyStreetNames = [
  'Cherrie Street',
  'Wallnut Avenue',
  'Oake Dr.',
  'Furn St.',
  'Redwould Ave.',
  'Why Toak Rd.',
]
const randStreetName = () => {
  return randArrEntry(dummyStreetNames);
}
const dummyCampusBuildings = [
  'Arts Building',
  'Legacy Building',
  'North Eastern Campus',
  'Green Campus',
  'Center for Educational Excellence',
]
const randCampusBuilding = () => {
  return randArrEntry(dummyCampusBuildings);
}

@Injectable({
  providedIn: 'root'
})
export class DemoDataService {

  public isSimulNewAccount: boolean;
  private accounts:Partial<IAccountInfo>[];
  private sessions:IAvailableSession[];
  private userInfo:IUserInfo;
  private sessionIdCounter = 100;
  private accountIdCounter = 100;

  constructor(
    private auth:AuthService,
  ) { 
    this.auth.user().subscribe(userInfo => this.userInfo = userInfo);
  }

  getAccounts(){
    if (!this.accounts){
      this.generateNewAccounts();
    }
    return this.accounts;
  }

  overrideAccountData(accounts){
    this.accounts = accounts;
    this.accounts.forEach(account => {
      if (!account.id){
        account.id = this.getNextAccountId();
      }
    })
    // console.log('overrideAccountData', this.accounts)
  }

  public getInvitationCode(){
    return getInvitCode();
  }
 
  public getTestSessions(){
    if (!this.sessions){
      this.generateTestSessions();
    }
    this.sortSessions();
    return this.sessions;
  }

  isMeAccommCoord(){
    let isMe = false;
    this.accounts.forEach(account => {
      if (account.isSelf){
        isMe = account.isAccommCoord;
      }
    })
    return isMe;
  }

  private sortBookingsByDate(bookings:IBooking[]){
    return bookings.sort( (a,b) => {
      const _a = a.timestamp.format('YYYY-MM-DD HH:mm');
      const _b = b.timestamp.format('YYYY-MM-DD HH:mm');
      if (_a == _b){ return 0; }
      if (_a < _b){ return -1; }
      return 1; 
    })
  }

  public sortSessions(){
    this.sessions.forEach(session => {
      session.__dateTimeStartStr = session.dateTimeStart.format('YYYY-MM-DD HH:mm');
    })
    this.sessions  = this.sessions.sort( (a,b) => {
      if (a.__dateTimeStartStr == b.__dateTimeStartStr){ return 0; }
      if (a.__dateTimeStartStr < b.__dateTimeStartStr){ return -1; }
      return 1; 
    })
    this.sessions.forEach( (session, i) => {
      session.__num = i + 1;
    })
  }

  getSessionById(sessionId:number){
    if (!this.sessions){
      this.generateTestSessions();
    }
    let session:IAvailableSession;
    this.sessions.forEach(_session =>{
      if (_session.id === sessionId){
        session = _session;
      }
    })
    return session;
  }

  public cancelSession(sessionId:number){
    let i = -1;
    this.sessions.forEach( (session, sessionIndex) => {
      if (session.id === sessionId){
        i = sessionIndex;
      }
    })
    if (i!== -1){
      this.sessions.splice(i, 1);
    }
  }

  private generateTestSessions(){
    this.getAccounts();
    this.sessions = [
      this.generateFakeTestSession(true, false, false, false),
    ]
    _.times(randInt(2,4), () => {
      this.sessions.push(this.generateFakeTestSession(false, coinFlip(), coinFlip(), coinFlip()))
    })
  }

  private getNextSessionId(){
    return this.sessionIdCounter ++;
  }

  private getNextAccountId(){
    return this.accountIdCounter ++;
  }

  private generateFakeTestSession(isStartingNow:boolean, hasWaitingList:boolean, isHidden:boolean = false, isAccessCodeEnabled:boolean=false){
    // to do assign invigilator
    const capacity = randInt(20, 50);
    let dateTimeStart;
    if (isStartingNow){
      const aFewMinutesAgoTime = [moment().format('H'), 0].join(':');
      dateTimeStart = moment(aFewMinutesAgoTime, 'H:m');
    }
    else{
      dateTimeStart = randomPastDate(-randInt(3, 10));
    }
    let accessCode = '';
    if (isAccessCodeEnabled){
      accessCode = randInt(10000, 1000000).toString(16)
    }
    const invitationCode = this.getInvitationCode();
    let bookings:IBooking[] = [];
    let waitlist:IBooking[] = [];
    let bookingsCount = 0;
    let waitlistCount = 0;
    if (hasWaitingList){
      bookingsCount = capacity;
      waitlistCount = randInt(1,4);
    }
    else{
      bookingsCount = randInt( Math.round(capacity*0.7), capacity-1);
    }
    _.times(bookingsCount, () => {
      bookings.push({
        name: randomName(),
        oct_id: ''+randInt(100000,999999),
        timestamp: randomPastDate(),
      });
    });
    _.times(waitlistCount, () => {
      waitlist.push({
        name: randomName(),
        oct_id: ''+randInt(100000,999999),
        timestamp: randomPastDate(),
      });
    })
    bookings = this.sortBookingsByDate(bookings);
    waitlist = this.sortBookingsByDate(waitlist);

    const invigilator:IAccountInfo = randArrEntry(this.accounts);
    invigilator.numSessions ++;

    return {
      __isStartingSoon: isStartingNow,
      __num: 7,
      id: this.getNextSessionId(),
      // location
      room: ''+randInt(100,600),
      campusBuilding: randCampusBuilding(),
      address: randInt(10,200) +' '+ randStreetName(),
      city: 'Toronto',
      province: 'ON', 
      postalCode: 'M1D 7J9',
      phoneNumber: '(647)'+randInt(100,900)+randInt(1000,9900),
      facultyId: 0,
      // time
      dateTimeStart,
      // capacity
      capacity,
      // restrictions
      isAccessCodeEnabled,
      accessCode,
      isHidden,
      // assigned invigil
      inivigilatorId: invigilator.id,
      // invitation code
      invitationCode,
      // bookings
      bookingsCount,
      bookings,
      waitlistCount,
      waitlist,
    }
  }

  getInvigilatorBasicInfo(invigilatorId:number){
    let account:Partial<IAccountInfo>;
    this.accounts.forEach(_account => {
      if (_account.id === invigilatorId){
        account = _account;
      }
    })
    if (account){
      if (account.isInviteUsed){
        return {
          id: account.id,
          name: account.firstName + ' ' + account.lastName,
          email: account.email
        }
      }
      else{
        return {
          id: account.id,
          name: account.invitationName,
          email: account.invitationEmail
        }
      }
    }
  }

  private generateNewAccounts(){
    this.accounts = [];
    if (this.userInfo){
      this.accounts.push({
        id: this.getNextAccountId(),
        isSelf: true,
        firstName: this.userInfo.firstName,
        lastName: this.userInfo.lastName,
        email: this.userInfo.email,
        phoneNumber: '(705)555-5555',
        isAccommCoord: false,
        numSessions: 0, // temp
        accountCreatedOn: randomPastDate(18), // temp
        accessGrantedOn: randomPastDate(20), // temp
        isInviteUsed: true,
      })
    }
    _.times(randInt(1,4) , ()=> {
      const isUsedInvite = true; // coinFlip();
      let identity = randomIdentity();
      this.accounts.push({
        id: this.getNextAccountId(),
        firstName: identity.firstName,
        lastName: identity.lastName,
        email: identity.email,
        phoneNumber: '(705)555-5555',
        isAccommCoord: false,
        numSessions: 0, //isUsedInvite ? randInt(0,4) : 0, // temp
        accountCreatedOn: randomPastDate(18), // temp
        invitationCode: getInvitCode(), // temp
        invitationName: identity.name,
        invitationEmail: identity.email,
        accessGrantedOn: randomPastDate(20), // temp
        isInviteUsed: isUsedInvite,
      });
    })
  }

}
